import type { FC } from 'react';
import { useEffect, useRef, useState , forwardRef } from 'react';
import { getS3PresignedUrl } from '@/utils/s3';
import { 
  Close, 
  Download, 
  Fullscreen, 
  FullscreenExit,
  ZoomIn,
  ZoomOut,
  RotateRight,
  Error as ErrorIcon,
  Image,
  PictureAsPdf,
  Description,
  VideoFile,
  AudioFile,
  InsertDriveFile,
  Slideshow,
  MoreHoriz,
  Edit
} from '@mui/icons-material';
import { 
  Box, 
  Button,
  Dialog, 
  DialogContent, 
  IconButton, 
  Typography, 
  CircularProgress,
  Alert,
  AlertTitle,
  Chip,
  Stack,
  Tooltip,
  Fade,
  Slide,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';

import type { TransitionProps } from '@mui/material/transitions';

import type { CommunityFile } from '@/types/community';
import type { File as FileV1 } from '@/types/file';
import type { File as FileV2 } from '@/types/v2/file';
import { EditFileDialog } from '@/pages/documents/components/edit-file-modal';
import { useDialog } from '@/hooks/use-dialog';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import axiosClient from '@/utils/client';
import toast from 'react-hot-toast';
import { ACCOUNT_ROLES } from '@/constants/account-roles';
import { COMMUNITY_ROLES } from '@/constants/community-roles';
import { RoleGuard } from '@/components/auth/guards/role-guard';

interface ViewDocumentDialogProps {
  open: boolean;
  onClose: () => void;
  file: CommunityFile | FileV1 | FileV2 | null;
}

// Custom transition for the dialog
const Transition = forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement<any, any>;
  },
  ref: React.Ref<HTMLDivElement>,
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const ViewDocumentDialog: FC<ViewDocumentDialogProps> = (props) => {

  
  const { open, onClose, file } = props;
  const [presignedUrl, setPresignedUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  
  const imageRef = useRef<HTMLImageElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const audioRef = useRef<HTMLAudioElement>(null);

  const editFileDialog = useDialog();
  const queryClient = useQueryClient();

  // Reset state when file changes
  useEffect(() => {
    if (file) {
      setIsLoading(true);
      setError(null);
      setPresignedUrl(null);
      setZoom(1);
      setRotation(0);
      

      
      fetchPresignedUrl();
    }
  }, [file]);



  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setMenuAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
  };

  const handleEditFile = () => {
    handleMenuClose();
    editFileDialog.handleOpen();
  };

  const handleFileEditSuccess = () => {
    // Refresh any relevant queries
    queryClient.invalidateQueries({ queryKey: ['folders'] });
    queryClient.invalidateQueries({ queryKey: ['files'] });
    toast.success('File updated successfully');
  };

  // Convert different file types to the format expected by EditFileDialog
  const convertFileForEdit = (): any => {
    if (!file) return null;
    
    // If it's already a File type, return as is
    if ('visibility' in file && 'mime_type' in file) {
      return file;
    }
    
    // If it's a CommunityFile, extract the nested file
    if ('file' in file && file.file) {
      return file.file;
    }
    
    // If it's a FileV2 or other type, create a minimal compatible object
    return {
      id: (file as any).id || (file as any).file?.id,
      name: (file as any).name || (file as any).file?.name,
      mime_type: (file as any).mime_type || (file as any).file?.mime_type,
      visibility: (file as any).visibility || (file as any).file?.visibility || 'public',
      size: (file as any).size || (file as any).file?.size,
      uploaded_by_id: (file as any).uploaded_by_id || (file as any).file?.uploaded_by_id,
      created_at: (file as any).created_at || (file as any).file?.created_at,
      path: (file as any).path || (file as any).file?.path,
      type: (file as any).type || (file as any).file?.type
    };
  };


  const fetchPresignedUrl = async () => {
    if (!file) return;
    
    try {
      setIsLoading(true);
      setError(null);
      // Get the file ID - try multiple possible locations
      const fileId = (file as any).id || (file as any).file?.id;
      
      if (!fileId) {
        throw new Error('No file ID found in file object');
      }
      
      console.log('File ID being used:', fileId);
      
      // Create a minimal file object with just the ID - like request attachment card
      const fileForApi = { id: fileId } as FileV2;
      
      console.log('File object being sent to getS3PresignedUrl:', fileForApi);
      const url = await getS3PresignedUrl(fileForApi);
      console.log('Presigned URL received:', url);
      setPresignedUrl(url);
      

    } catch (err) {
      console.error('Error fetching presigned URL:', err);
      setError('Failed to load document. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDownload = () => {
    if (presignedUrl && file) {
      const link = document.createElement('a');
      link.href = presignedUrl;
      link.download = getFileName();
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const getFileName = () => {
    return (file as any)?.file?.name || (file as any)?.name || 'document';
  };

  const getFileDescription = () => {
    return (file as any)?.description || '';
  };

  const getFileType = () => {
    // First try to get mime_type from the file object
    let mimeType = (file as any)?.mime_type || (file as any)?.file?.mime_type;
    
    // If no mime_type, try to infer from filename extension
    if (!mimeType && (file as any)?.name) {
      const fileName = (file as any)?.name || (file as any)?.file?.name || '';
      const extension = fileName.split('.').pop()?.toLowerCase();
      
      if (extension) {
        switch (extension) {
          case 'jpg':
          case 'jpeg':
          case 'png':
          case 'gif':
          case 'bmp':
          case 'webp':
          case 'svg':
            mimeType = `image/${  extension}`;
            break;
          case 'pdf':
            mimeType = 'application/pdf';
            break;
          case 'pptx':
          case 'ppt':
            mimeType = 'application/vnd.openxmlformats-officedocument.presentationml.presentation';
            break;
          case 'docx':
          case 'doc':
            mimeType = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
            break;
          case 'xlsx':
          case 'xls':
            mimeType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
            break;
          case 'mp4':
          case 'avi':
          case 'mov':
          case 'wmv':
          case 'flv':
          case 'webm':
            mimeType = `video/${  extension}`;
            break;
          case 'mp3':
          case 'wav':
          case 'ogg':
          case 'aac':
            mimeType = `audio/${  extension}`;
            break;
          case 'txt':
          case 'md':
          case 'html':
          case 'css':
          case 'js':
            mimeType = `text/${  extension}`;
            break;
        }
      }
    }
    
    if (!mimeType) return 'unknown';
    
    // Handle both full MIME types and shortened formats
    if (mimeType.includes('pdf') || mimeType === 'pdf') return 'pdf';
    if (mimeType.includes('image') || mimeType === 'jpeg' || mimeType === 'jpg' || mimeType === 'png' || mimeType === 'gif' || mimeType === 'bmp' || mimeType === 'webp' || mimeType === 'svg') return 'image';
    if (mimeType.includes('video') || mimeType === 'mp4' || mimeType === 'avi' || mimeType === 'mov' || mimeType === 'wmv' || mimeType === 'flv' || mimeType === 'webm') return 'video';
    if (mimeType.includes('audio') || mimeType === 'mp3' || mimeType === 'wav' || mimeType === 'ogg' || mimeType === 'aac') return 'audio';
    if (mimeType.includes('text') || mimeType === 'txt' || mimeType === 'md' || mimeType === 'html' || mimeType === 'css' || mimeType === 'js') return 'text';
    if (mimeType.includes('presentation') || mimeType.includes('powerpoint') || mimeType.includes('pptx') || mimeType.includes('ppt')) return 'presentation';
    if (mimeType.includes('document') || mimeType.includes('word') || mimeType.includes('docx') || mimeType.includes('doc')) return 'document';
    if (mimeType.includes('spreadsheet') || mimeType.includes('excel') || mimeType.includes('xlsx') || mimeType.includes('xls')) return 'spreadsheet';
    return 'document';
  };

  const getFileIcon = () => {
    const fileType = getFileType();
    switch (fileType) {
      case 'pdf': return <PictureAsPdf />;
      case 'image': return <Image />;
      case 'video': return <VideoFile />;
      case 'audio': return <AudioFile />;
      case 'text': return <Description />;
      case 'presentation': return <Slideshow />;
      case 'document': return <Description />;
      case 'spreadsheet': return <InsertDriveFile />;
      default: return <InsertDriveFile />;
    }
  };

  const getFileTypeLabel = () => {
    const fileType = getFileType();
    switch (fileType) {
      case 'pdf': return 'PDF Document';
      case 'image': return 'Image';
      case 'video': return 'Video';
      case 'audio': return 'Audio';
      case 'text': return 'Text Document';
      case 'presentation': return 'PowerPoint Presentation';
      case 'document': return 'Word Document';
      case 'spreadsheet': return 'Excel Spreadsheet';
      default: return 'Document';
    }
  };

  const handleZoomIn = () => { setZoom(prev => Math.min(prev + 0.25, 3)); };
  const handleZoomOut = () => { setZoom(prev => Math.max(prev - 0.25, 0.5)); };
  const handleRotate = () => { setRotation(prev => (prev + 90) % 360); };
  const handleFullscreen = () => { setIsFullscreen(!isFullscreen); };



  const renderContent = () => {
    if (isLoading) {
      return (
        <Box 
          alignItems="center" 
          display="flex" 
          flexDirection="column" 
          gap={2} 
          justifyContent="center"
          minHeight="400px"
        >
          <CircularProgress size={60} />
          <Typography color="text.secondary" variant="h6">
            Loading document...
          </Typography>
        </Box>
      );
    }

    if (error) {
      return (
        <Box 
          alignItems="center" 
          display="flex" 
          flexDirection="column" 
          gap={2} 
          justifyContent="center"
          minHeight="400px"
        >
          <ErrorIcon color="error" sx={{ fontSize: 60 }} />
          <Alert severity="error" sx={{ maxWidth: 400 }}>
            <AlertTitle>Error Loading Document</AlertTitle>
            {error}
          </Alert>
          <IconButton 
            color="primary" 
            onClick={fetchPresignedUrl} 
            size="large"
            sx={{ mt: 2 }}
          >
            <RotateRight />
          </IconButton>
        </Box>
      );
    }

    if (!presignedUrl) return null;

        const fileType = getFileType();
    
    switch (fileType) {
      case 'pdf':
        return (
          <Box sx={{ 
            width: '100%', 
            height: '100%',
            overflow: 'auto', // Add scrolling for smaller screens
            display: 'flex',
            flexDirection: 'column'
          }}>
            {/* PDF Viewer - using exact same approach as request attachment card */}
            <Box sx={{ flexShrink: 0 }}> {/* Prevent iframe from shrinking */}
              <iframe 
                onError={(e) => {
                  console.error('PDF iframe error:', e);
                  setError('Failed to load PDF');
                }} 
                onLoad={() => {
                  console.log('PDF iframe loaded successfully');
                  setIsLoading(false);
                }} 
                src={presignedUrl}
                style={{ 
                  width: '100%', 
                  height: '80vh', 
                  border: 'none' 
                }}
                title={getFileName()}
              />
            </Box>
            
            {/* Simple controls below the iframe */}
            <Box 
              sx={{ 
                p: 2, 
                borderTop: '1px solid', 
                borderColor: 'divider',
                backgroundColor: 'background.paper',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexShrink: 0 // Prevent controls from shrinking
              }}
            >
              <Typography color="text.secondary" variant="body2">
                PDF Document
              </Typography>
              <Stack direction="row" spacing={1}>
                <Tooltip title="Open in new tab">
                  <IconButton 
                    color="primary"
                    onClick={() => window.open(presignedUrl, '_blank')}
                    size="small"
                  >
                    <Fullscreen />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Download PDF">
                  <IconButton 
                    color="primary"
                    onClick={handleDownload}
                    size="small"
                  >
                    <Download />
                  </IconButton>
                </Tooltip>
              </Stack>
            </Box>
          </Box>
        );

      case 'image':
        return (
          <Box 
            sx={{ 
              width: '100%', 
              height: '100%', 
              minHeight: '600px',
              display: 'flex',
              flexDirection: 'column'
            }}
          >
            {/* Image Viewer */}
            <Box 
              sx={{ 
                flex: 1, 
                display: 'flex', 
                justifyContent: 'center', 
                alignItems: 'center',
                overflow: 'hidden',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1
              }}
            >
              <img
                alt={getFileName()}
                onError={() => {
                  setError('Failed to load image');
                }}
                onLoad={() => {
                  setIsLoading(false);
                }}
                ref={imageRef}
                src={presignedUrl}
                style={{
                  maxWidth: '100%',
                  maxHeight: '100%',
                  transform: `scale(${zoom}) rotate(${rotation}deg)`,
                  transition: 'transform 0.3s ease-in-out',
                  cursor: 'pointer'
                }}
              />
            </Box>
            
            {/* Image Controls */}
            <Box 
              sx={{ 
                p: 2, 
                borderTop: '1px solid', 
                borderColor: 'divider',
                backgroundColor: 'background.paper',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}
            >
              <Stack alignItems="center" direction="row" spacing={1}>
                <Typography color="text.secondary" variant="body2">
                  Image
                </Typography>
                <Stack direction="row" spacing={1}>
                  <Tooltip title="Zoom In">
                    <IconButton onClick={handleZoomIn} size="small">
                      <ZoomIn />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Zoom Out">
                    <IconButton onClick={handleZoomOut} size="small">
                      <ZoomOut />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Rotate">
                    <IconButton onClick={handleRotate} size="small">
                      <RotateRight />
                    </IconButton>
                  </Tooltip>
                </Stack>
              </Stack>
              
              <Stack direction="row" spacing={1}>
                <Tooltip title="Open in new tab">
                  <IconButton 
                    color="primary"
                    onClick={() => window.open(presignedUrl, '_blank')}
                    size="small"
                  >
                    <Fullscreen />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Download Image">
                  <IconButton 
                    color="primary"
                    onClick={handleDownload}
                    size="small"
                  >
                    <Download />
                  </IconButton>
                </Tooltip>
              </Stack>
            </Box>
          </Box>
        );

      case 'video':
        return (
          <Box 
            sx={{ 
              display: 'flex', 
              justifyContent: 'center', 
              alignItems: 'center',
              minHeight: '400px'
            }}
          >
            <video
              controls
              onError={() => { setError('Failed to load video'); }}
              onLoadStart={() => { setIsLoading(false); }}
              ref={videoRef}
              style={{
                maxWidth: '100%',
                maxHeight: '100%',
                borderRadius: '8px'
              }}
            >
              <source src={presignedUrl} type={(file as any)?.mime_type || (file as any)?.file?.mime_type} />
              Your browser does not support the video tag.
            </video>
          </Box>
        );

      case 'audio':
        return (
          <Box 
            sx={{ 
              display: 'flex', 
              justifyContent: 'center', 
              alignItems: 'center',
              minHeight: '200px'
            }}
          >
            <audio
              controls
              onError={() => { setError('Failed to load audio'); }}
              onLoadStart={() => { setIsLoading(false); }}
              ref={audioRef}
              style={{
                width: '100%',
                maxWidth: '400px'
              }}
            >
              <source src={presignedUrl} type={(file as any)?.mime_type || (file as any)?.file?.mime_type} />
              Your browser does not support the audio tag.
            </audio>
          </Box>
        );

      case 'text':
        return (
          <Box 
            sx={{ 
              display: 'flex', 
              flexDirection: 'column',
              alignItems: 'center', 
              justifyContent: 'center',
              minHeight: '400px',
              gap: 2,
              p: 4,
              border: '2px dashed',
              borderColor: 'divider',
              borderRadius: 2
            }}
          >
            <Description sx={{ fontSize: 80, color: 'text.secondary' }} />
            <Typography color="text.secondary" variant="h6">
              Text Document
            </Typography>
            <Typography color="text.secondary" textAlign="center" variant="body2">
              Text files can be downloaded and viewed in any text editor.
            </Typography>
            <Stack direction="row" justifyContent="center" spacing={2} sx={{ mt: 2 }}>
              <Button
                onClick={handleDownload}
                startIcon={<Download />}
                variant="outlined"
              >
                Download Text File
              </Button>
            </Stack>
          </Box>
        );

      case 'presentation':
        return (
          <Box 
            sx={{ 
              display: 'flex', 
              flexDirection: 'column',
              alignItems: 'center', 
              justifyContent: 'center',
              minHeight: '400px',
              gap: 2,
              p: 4,
              border: '2px dashed',
              borderColor: 'divider',
              borderRadius: 2
            }}
          >
            <Slideshow sx={{ fontSize: 80, color: 'text.secondary' }} />
            <Typography color="text.secondary" variant="h6">
              PowerPoint Preview
            </Typography>
            <Typography color="text.secondary" textAlign="center" variant="body2">
              PowerPoint files can be viewed using Microsoft Office Online or downloaded to view locally.
            </Typography>
            <Stack direction="row" justifyContent="center" spacing={2} sx={{ mt: 2 }}>
              <Button
                onClick={() => window.open(`https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(presignedUrl)}`, '_blank')}
                startIcon={<Fullscreen />}
                variant="contained"
              >
                View Online
              </Button>
              <Button
                onClick={handleDownload}
                startIcon={<Download />}
                variant="outlined"
              >
                Download
              </Button>
            </Stack>
          </Box>
        );

      case 'document':
        return (
          <Box 
            sx={{ 
              display: 'flex', 
              flexDirection: 'column',
              alignItems: 'center', 
              justifyContent: 'center',
              minHeight: '400px',
              gap: 2,
              p: 4,
              border: '2px dashed',
              borderColor: 'divider',
              borderRadius: 2
            }}
          >
            <Description sx={{ fontSize: 80, color: 'text.secondary' }} />
            <Typography color="text.secondary" variant="h6">
              Word Document Preview
            </Typography>
            <Typography color="text.secondary" textAlign="center" variant="body2">
              Word documents can be viewed using Microsoft Office Online or downloaded to view locally.
            </Typography>
            <Stack direction="row" justifyContent="center" spacing={2} sx={{ mt: 2 }}>
              <Button
                onClick={() => window.open(`https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(presignedUrl)}`, '_blank')}
                startIcon={<Fullscreen />}
                variant="contained"
              >
                View Online
              </Button>
              <Button
                onClick={handleDownload}
                startIcon={<Download />}
                variant="outlined"
              >
                Download
              </Button>
            </Stack>
          </Box>
        );

      case 'spreadsheet':
        return (
          <Box 
            sx={{ 
              display: 'flex', 
              flexDirection: 'column',
              alignItems: 'center', 
              justifyContent: 'center',
              minHeight: '400px',
              gap: 2,
              p: 4,
              border: '2px dashed',
              borderColor: 'divider',
              borderRadius: 2
            }}
          >
            <InsertDriveFile sx={{ fontSize: 80, color: 'text.secondary' }} />
            <Typography color="text.secondary" variant="h6">
              Excel Spreadsheet Preview
            </Typography>
            <Typography color="text.secondary" textAlign="center" variant="body2">
              Excel files can be viewed using Microsoft Office Online or downloaded to view locally.
            </Typography>
            <Stack direction="row" justifyContent="center" spacing={2} sx={{ mt: 2 }}>
              <Button
                onClick={() => window.open(`https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(presignedUrl)}`, '_blank')}
                startIcon={<Fullscreen />}
                variant="contained"
              >
                View Online
              </Button>
              <Button
                onClick={handleDownload}
                startIcon={<Download />}
                variant="outlined"
              >
                Download
              </Button>
            </Stack>
          </Box>
        );

      default:
        console.log('❌ Falling through to default case - fileType was:', fileType);
        console.log('❌ File object:', file);
        return (
          <Box 
            sx={{ 
              display: 'flex', 
              flexDirection: 'column',
              alignItems: 'center', 
              justifyContent: 'center',
              minHeight: '400px',
              gap: 2,
              p: 4,
              border: '2px dashed',
              borderColor: 'divider',
              borderRadius: 2
            }}
          >
            <InsertDriveFile sx={{ fontSize: 80, color: 'text.secondary' }} />
            <Typography color="text.secondary" variant="h6">
              Preview not available
            </Typography>
            <Typography color="text.secondary" textAlign="center" variant="body2">
              This file type cannot be previewed. Use the download button to view the file.
            </Typography>
            <Typography color="text.secondary" textAlign="center" variant="caption">
              Debug: File type detected as "{fileType}"
            </Typography>
          </Box>
        );
    }
  };

  if (!file) return null;

  return (
    <>
      <Dialog 
        PaperProps={{
          sx: {
            minHeight: isFullscreen ? '100vh' : '80vh',
            maxHeight: isFullscreen ? '100vh' : '90vh'
          }
        }} 
        TransitionComponent={Transition} 
        fullScreen={isFullscreen}
        fullWidth 
        maxWidth={isFullscreen ? false : "lg"}
        onClose={onClose}
        open={open}
      >
      <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column', height: '100%' }}>
        {/* Header */}
        <Box 
          sx={{ 
            p: 3, 
            borderBottom: '1px solid', 
            borderColor: 'divider',
            backgroundColor: 'background.paper'
          }}
        >
          <Stack alignItems="flex-start" direction="row" justifyContent="space-between" spacing={2}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Stack alignItems="center" direction="row" mb={1} spacing={2}>
                <Box sx={{ color: 'primary.main' }}>
                  {getFileIcon()}
                </Box>
                <Chip 
                  color="primary" 
                  label={getFileTypeLabel()} 
                  size="small"
                  variant="outlined"
                />
              </Stack>
              <Typography component="h2" noWrap variant="h5">
                {getFileName()}
              </Typography>
              {getFileDescription() && (
                <Typography color="text.secondary" sx={{ mt: 1 }} variant="body2">
                  {getFileDescription()}
                </Typography>
              )}
            </Box>
            
            <Stack direction="row" spacing={1}>
              {/* Zoom Controls - Only for images */}
              {getFileType() === 'image' && (
                <>
                  <Tooltip title="Zoom Out">
                    <IconButton disabled={zoom <= 0.5} onClick={handleZoomOut}>
                      <ZoomOut />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Zoom In">
                    <IconButton disabled={zoom >= 3} onClick={handleZoomIn}>
                      <ZoomIn />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Rotate">
                    <IconButton onClick={handleRotate}>
                      <RotateRight />
                    </IconButton>
                  </Tooltip>
                </>
              )}
              
              <Tooltip title="Download">
                <IconButton color="primary" onClick={handleDownload}>
                  <Download />
                </IconButton>
              </Tooltip>
              
              <Tooltip title={isFullscreen ? "Exit Fullscreen" : "Fullscreen"}>
                <IconButton color="primary" onClick={handleFullscreen}>
                  {isFullscreen ? <FullscreenExit /> : <Fullscreen />}
                </IconButton>
              </Tooltip>
              
              {/* Three dot menu for file actions */}
              <RoleGuard
                requiredAccountRoles={[
                  ACCOUNT_ROLES.ACCOUNT_ADMIN,
                  ACCOUNT_ROLES.ACCOUNT_MANAGER,
                  ACCOUNT_ROLES.ACCOUNT_OWNER,
                ]}
                requiredCommunityRoles={[COMMUNITY_ROLES.COMMUNITY_MANAGER]}
              >
                <Tooltip title="More options">
                  <IconButton 
                    color="primary"
                    onClick={handleMenuOpen}
                  >
                    <MoreHoriz />
                  </IconButton>
                </Tooltip>
              </RoleGuard>
              
              <Tooltip title="Close">
                <IconButton color="primary" onClick={onClose}>
                  <Close />
                </IconButton>
              </Tooltip>
            </Stack>
          </Stack>
        </Box>

        {/* Content */}
        <Box 
          sx={{ 
            flex: 1, 
            display: 'flex', 
            flexDirection: 'column',
            overflow: 'auto', // Changed from 'hidden' to 'auto' to enable scrolling
            p: 3
          }}
        >
          <Fade in={!isLoading} timeout={300}>
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
              {renderContent()}
            </Box>
          </Fade>
        </Box>
      </DialogContent>
      
      {/* Three dot menu */}
      <Menu
        anchorEl={menuAnchorEl}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        onClick={handleMenuClose}
        onClose={handleMenuClose}
        open={Boolean(menuAnchorEl)}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
      >
        <MenuItem onClick={handleEditFile}>
          <ListItemIcon>
            <Edit fontSize="small" />
          </ListItemIcon>
          <ListItemText>Edit File</ListItemText>
        </MenuItem>
      </Menu>
          </Dialog>
    
      {/* Edit File Dialog - rendered outside the ViewDocument dialog to avoid nesting issues */}
      <EditFileDialog
        file={convertFileForEdit()}
        onClose={editFileDialog.handleClose}
        onEdit={handleFileEditSuccess}
        onSuccess={handleFileEditSuccess}
        open={editFileDialog.open}
      />
    </>
  );
};

export default ViewDocumentDialog;
